/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import SickbayResolution from 'linced-app/enums/sickbay-resolution';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import moment from 'moment';

export default class SickbayDelete extends Component {
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  session;

  @service
  dates;

  @service
  toaster;

  @tracked
  dialogOpen = false;
  sickbayVisit = null;
  @tracked
  deleteInProgress = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @alias('attendanceCodes.medicalAbsence.code')
  medicalCode;

  @alias('attendanceCodes.present.code')
  presentCode;

  @alias('attendanceCodes.sickbay.code')
  sickbayCode;

  getTimestamp(time) {
    let t = moment(`${this.sickbayVisit.date} ${time}`, 'YYYY-MM-DD HH:mm');
    return t.unix();
  }

  deleteMovement(period, time, code) {
    return this.attendance
      .recordForPeriod(
        this.sickbayVisit.get('learner.id'),
        this.sickbayVisit.date,
        period,
      )
      .then((record) => {
        if (!isEmpty(record)) {
          let foundMovement = record.movements.find((movement) => {
            return movement.time === time && movement.code === code;
          });
          if (isEmpty(foundMovement)) {
            // No matching movement.  Don't delete anything.
            return Promise.resolve();
          }
          if (record.movements.length === 1) {
            // This is the only movement.  Delete the whole record.
            record.set('auditReason', 'Sickbay visit deleted');
            return record.destroyRecord();
          }

          // The record has multiple movements.  Delete just the one.
          record.movements.removeObject(foundMovement);
          record.setProperties({
            effectiveFrom: time,
            reason: 'Sickbay visit deleted',
          });
          return record.save();
        }
      });
  }

  @action
  openDialog() {
    this.dialogOpen = true;
  }

  @action
  cancel() {
    this.dialogOpen = false;
  }

  @action
  delete() {
    this.deleteInProgress = true;
    return Promise.resolve()
      .then(() => {
        // Remove the linked post, if there is one.
        if (this.sickbayVisit.get('post.id')) {
          return this.sickbayVisit
            .get('post')
            .then((post) => {
              return post.destroyRecord();
            })
            .catch(() => {
              // If the post doesn't exist, we should still proceed.
              return Promise.resolve();
            });
        }
      })
      .then(() => {
        // Remove any associated attendance records/movements, if applicable.
        if (this.trackMovements) {
          let attendanceChain = Promise.resolve();

          // Start with check-out time, if applicable.
          if (this.sickbayVisit.checkOutTime) {
            attendanceChain = attendanceChain.then(async () => {
              let period = await this.dates.periodForUnixTime(
                this.sickbayVisit.checkOutTime,
              );
              let code = this.medicalCode;
              if (
                this.sickbayVisit.resolution ===
                SickbayResolution.RETURNED_TO_CLASS_BREAK
              ) {
                code = this.presentCode;
              }
              if (!isEmpty(period)) {
                return this.deleteMovement(
                  period,
                  this.sickbayVisit.checkOutTime,
                  code,
                );
              } else {
                period = await this.dates.periodAfterUnixTime(
                  this.sickbayVisit.checkOutTime,
                );
                if (!isEmpty(period)) {
                  return this.deleteMovement(
                    period,
                    this.getTimestamp(period.startTime),
                    code,
                  );
                }
              }
            });
          }

          // Then, process check-in time, if the school has a "sickbay"
          // attendance code.
          if (!isEmpty(this.sickbayCode)) {
            attendanceChain = attendanceChain.then(async () => {
              let period = await this.dates.periodForUnixTime(
                this.sickbayVisit.checkInTime,
              );
              if (!isEmpty(period)) {
                return this.deleteMovement(
                  period,
                  this.sickbayVisit.checkInTime,
                  this.sickbayCode,
                );
              } else {
                period = await this.dates.periodAfterUnixTime(
                  this.sickbayVisit.checkInTime,
                );
                if (!isEmpty(period)) {
                  return this.deleteMovement(
                    period,
                    this.getTimestamp(period.startTime),
                    this.sickbayCode,
                  );
                }
              }
            });
          }

          return attendanceChain;
        }
      })
      .then(() => {
        return this.sickbayVisit.destroyRecord();
      })
      .then(() => {
        this.toaster.success('Sickbay visit deleted successfully!');
      })
      .catch(() => {
        this.toaster.error('Sickbay visit delete failed!');
      })
      .finally(() => {
        // A successful destroyRecord() removes the sickbay visit from the
        // store, which immediately tears down this row (and this
        // component) via the list's {{#each}}. This callback can still
        // fire afterward, so guard against setting properties on an
        // already-destroyed component.
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.dialogOpen = false;
        this.deleteInProgress = false;
      });
  }
}
